import React, { useState } from "react";
import LoadingButton from "../../components/common/LoadingButton";
import V2AuthLayout from "../components/V2AuthLayout";
import { useV2Theme } from "../theme/ThemeProvider";
import { exchangeGoogleSso, persistAuthSession, parseAuthApiError } from "../auth/googleSso";
import { DEFAULT_ORGANIZATION_SIZE } from "../utils/signupDefaults";

const inputClass =
  "mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/25";

/**
 * Complete Google trial signup after AUTH_SSO_NEEDS_SIGNUP_DETAILS.
 */
export default function V2GoogleCompleteSignupPage({
  pendingGoogle,
  onSuccess,
  onCancel,
}) {
  const { tokens } = useV2Theme();
  const [organizationName, setOrganizationName] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!pendingGoogle?.idToken) {
      setError("Google session expired. Please try Continue with Google again.");
      return;
    }
    if (!organizationName.trim()) {
      setError("Organisation name is required");
      return;
    }
    if (!termsAccepted) {
      setError("You must accept the Terms and Privacy Policy to continue");
      return;
    }

    setLoading(true);
    try {
      const now = new Date().toISOString();
      const { response, data } = await exchangeGoogleSso({
        id_token: pendingGoogle.idToken,
        organization_name: organizationName.trim(),
        organization_size: DEFAULT_ORGANIZATION_SIZE,
        terms_accepted: true,
        gdpr_consent: true,
        terms_accepted_at: now,
        gdpr_consent_at: now,
      });
      if (response.ok) {
        persistAuthSession(data);
        onSuccess?.(data.user);
      } else {
        setError(parseAuthApiError(data, "Could not complete Google signup"));
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <V2AuthLayout
      title="Finish your Google trial"
      subtitle={
        pendingGoogle?.email
          ? `Signed in as ${pendingGoogle.email}`
          : "Tell us about your workspace"
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit}>
        <div>
          <label
            htmlFor="organization_name"
            className="block text-sm font-medium text-slate-700"
          >
            Organisation name *
          </label>
          <input
            id="organization_name"
            name="organization_name"
            type="text"
            required
            className={inputClass}
            placeholder="Company or team"
            value={organizationName}
            onChange={(e) => setOrganizationName(e.target.value)}
            autoComplete="organization"
          />
        </div>

        <label className="flex items-start gap-3 text-sm text-slate-700">
          <input
            type="checkbox"
            required
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-700 focus:ring-blue-500"
          />
          <span>
            I accept the{" "}
            <a
              href="https://decisionpen.com/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-700 underline hover:text-blue-600"
            >
              Terms and Conditions
            </a>{" "}
            and{" "}
            <a
              href="https://decisionpen.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-700 underline hover:text-blue-600"
            >
              Privacy Policy
            </a>{" "}
            *
          </span>
        </label>

        {error ? (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {error}
          </div>
        ) : null}

        <LoadingButton
          type="submit"
          loading={loading}
          loadingLabel="Creating trial…"
          className={`w-full ${tokens.button.primary} disabled:opacity-50`}
        >
          Create trial workspace
        </LoadingButton>

        <p className="text-center text-sm text-slate-600">
          <button
            type="button"
            onClick={onCancel}
            className="font-medium text-blue-700 hover:text-blue-600"
          >
            Back to sign in
          </button>
        </p>
      </form>
    </V2AuthLayout>
  );
}
